import { z } from 'zod';
import { booleanish, optionalText, paginationQuery, requiredText } from './common';
import { BR_STATES, isValidCPF, normalizePhone } from '../validators';

/**
 * Documento do fornecedor: CPF (11 dígitos) ou CNPJ (14 posições). Desde julho de 2026 o
 * CNPJ pode ser ALFANUMÉRICO (IN RFB 2.229/2024): 12 posições com letras ou números e os
 * 2 dígitos verificadores numéricos. O cálculo é o mesmo módulo 11, com cada caractere
 * valendo (código ASCII - 48); para CNPJ só de números o resultado é o de sempre.
 */
export function normalizeTaxId(value: string | null | undefined): string {
  return (value ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '');
}

export function isValidCnpjAlnum(value: string | null | undefined): boolean {
  const c = normalizeTaxId(value);
  if (!/^[0-9A-Z]{12}\d{2}$/.test(c) || /^(\d)\1{13}$/.test(c)) return false;
  const v = (ch: string) => ch.charCodeAt(0) - 48;
  const calc = (len: number) => {
    const weights = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < len; i++) sum += v(c[i]!) * weights[i]!;
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return calc(12) === Number(c[12]) && calc(13) === Number(c[13]);
}

/** CPF ou CNPJ (numérico ou alfanumérico) com dígitos verificadores corretos. */
export function isValidTaxId(value: string | null | undefined): boolean {
  const d = normalizeTaxId(value);
  if (d.length === 11) return /^\d{11}$/.test(d) && isValidCPF(d);
  if (d.length === 14) return isValidCnpjAlnum(d);
  return false;
}

/** "12345678000195" -> "12.345.678/0001-95"; CPF -> "123.456.789-09"; alfanumérico no mesmo desenho. */
export function formatTaxId(value: string | null | undefined): string {
  const d = normalizeTaxId(value);
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\w{2})(\w{3})(\w{3})(\w{4})(\w{2})/, '$1.$2.$3/$4-$5');
  return value ?? '';
}

const taxId = z
  .string()
  .trim()
  .max(30)
  .nullish()
  .transform((v) => (v ? normalizeTaxId(v) : null))
  .refine((v) => v === null || isValidTaxId(v), 'CNPJ ou CPF inválido. Confira os números.');

const phone = z
  .string()
  .trim()
  .max(30)
  .nullish()
  .transform((v) => (v ? v : null))
  .refine((v) => v === null || normalizePhone(v) !== null, 'Telefone inválido. Use DDD e número.')
  .transform((v) => (v ? normalizePhone(v) : null));

export const supplierInputSchema = z.object({
  name: requiredText(120, 'Informe o nome do fornecedor'),
  legalName: optionalText(160),
  document: taxId,
  contactName: optionalText(120),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .nullish()
    .transform((v) => (v ? v : null))
    .refine((v) => v === null || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v), 'E-mail inválido'),
  phone,
  whatsapp: phone,
  zipCode: z
    .string()
    .trim()
    .max(9)
    .nullish()
    .transform((v) => (v ? v.replace(/\D/g, '') : null))
    .refine((v) => v === null || v.length === 8, 'Informe o CEP com 8 números'),
  street: optionalText(160),
  number: optionalText(20),
  district: optionalText(80),
  city: optionalText(80),
  state: z
    .string()
    .trim()
    .toUpperCase()
    .nullish()
    .transform((v) => (v ? v : null))
    .refine((v) => v === null || (BR_STATES as readonly string[]).includes(v), 'UF inválida'),
  notes: optionalText(1000),
  isActive: z.boolean().default(true),
  /** Cadastrar mesmo existindo outro fornecedor com o mesmo CNPJ/CPF (aviso confirmado). */
  allowDuplicate: z.boolean().optional(),
});
export type SupplierInput = z.infer<typeof supplierInputSchema>;

export const supplierUpdateSchema = supplierInputSchema.partial();
export type SupplierUpdateInput = z.infer<typeof supplierUpdateSchema>;

export const supplierListQuery = paginationQuery.extend({
  q: z.string().trim().max(100).optional(),
  /** Busca exata pelo CNPJ/CPF (com ou sem pontuação). */
  document: z.string().trim().max(30).optional(),
  active: booleanish.optional(),
  /** Inclui os excluídos (para restaurar). */
  deleted: booleanish.optional(),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type SupplierListQuery = z.infer<typeof supplierListQuery>;
